import { useEffect, useRef } from "react";

import axios from "axios";

import { toastWarning } from "@alpacahq/ui/old";

import { shouldUseTokenizedEquityMarketData } from "~/v2/helpers/tokenizedEquityTrading";
import type { Product } from "../../rest/account";
import type { Quote } from "../../rest/data";
import { useGetIsSubscribedToMarketData } from "../useGetIsSubscribedToMarketData";
import { useGetMarketStatus } from "../useGetMarketStatus";
import { useGetQuote } from "../useGetQuote";
import { useGetSipSnapshot } from "../useGetSipSnapshot";
import {
  getFeedLabel,
  getReviewFeed,
  usesSipSnapshotEndpoint,
} from "../useMarketDataFeed";

interface UseReviewQuoteArgs {
  symbol: string;
  product: Product | undefined;
  isOvernightTradable: boolean;
  isInReview: boolean;
}

interface ReviewQuote {
  reviewQuote: Quote | undefined;
  reviewFeedLabel: string;
  isReviewQuoteLoading: boolean;
  onRefreshReviewQuote: () => void;
}

/**
 * Single source of truth for fetching a quote during the order ticket review
 * stage. Picks the correct feed via `getReviewFeed` and routes to the correct
 * endpoint: unsubscribed live reviews use the dedicated SIP snapshot endpoint,
 * everything else uses the normal quote endpoint.
 */
export const useOrderTicketReviewQuote = ({
  symbol,
  product,
  isOvernightTradable,
  isInReview,
}: UseReviewQuoteArgs): ReviewQuote => {
  const { isSubscribed, isLoading: isSubscribedLoading } =
    useGetIsSubscribedToMarketData();
  const { marketStatus } = useGetMarketStatus();

  const feed = getReviewFeed({
    product,
    phase: marketStatus?.phase,
    isOvernightTradable,
    isSubscribed,
  });
  // Tokenized equity quotes come from us-1 crypto snapshots, not the equity SIP endpoint.
  const usesTokenizedEquityMarketData =
    shouldUseTokenizedEquityMarketData(symbol);
  const shouldUseSipSnapshot =
    usesSipSnapshotEndpoint({ isSubscribed, feed }) &&
    !usesTokenizedEquityMarketData;
  const isEnabled = isInReview && !isSubscribedLoading && !!symbol;

  const sharedOptions = {
    refetchOnWindowFocus: false,
    refetchInterval: false as const,
    retry: false,
  };

  const snapshot = useGetSipSnapshot({
    symbol,
    options: { ...sharedOptions, enabled: isEnabled && shouldUseSipSnapshot },
  });

  const generic = useGetQuote({
    symbol,
    feed,
    options: { ...sharedOptions, enabled: isEnabled && !shouldUseSipSnapshot },
  });

  const reviewQuote = shouldUseSipSnapshot ? snapshot.sipQuote : generic.quote;
  const isReviewQuoteLoading = shouldUseSipSnapshot
    ? snapshot.isLoading || snapshot.isFetching
    : generic.isLoading || generic.isFetching;

  // v5 removed useQuery's per-query onError; react to whichever of
  // snapshot/generic is currently active instead.
  const activeError = shouldUseSipSnapshot ? snapshot.error : generic.error;
  const activeErrorUpdatedAt = shouldUseSipSnapshot
    ? snapshot.errorUpdatedAt
    : generic.errorUpdatedAt;
  const handledErrorAt = useRef<number>();

  useEffect(() => {
    if (
      !activeError ||
      !activeErrorUpdatedAt ||
      handledErrorAt.current === activeErrorUpdatedAt
    ) {
      return;
    }
    handledErrorAt.current = activeErrorUpdatedAt;
    if (
      axios.isAxiosError(activeError) &&
      activeError.response?.status === 429
    ) {
      toastWarning("Quote refresh limit reached. Try again shortly.");
    }
  }, [activeError, activeErrorUpdatedAt]);

  const onRefreshReviewQuote = () => {
    if (shouldUseSipSnapshot) {
      snapshot.refetch();
    } else {
      generic.refetch();
    }
  };

  return {
    reviewQuote,
    reviewFeedLabel: getFeedLabel(feed),
    isReviewQuoteLoading,
    onRefreshReviewQuote,
  };
};

export default useOrderTicketReviewQuote;
